import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Copy, Search, Link2, ShoppingBag, Coins, Bot, Store, Library, Webhook, Globe, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { API_HOST, DOC_LINKS, STARTER_PROMPT, DEVELOPER_SIGNUP } from "@/constants/developers";

const ENDPOINTS = [
  {
    icon: Search,
    path: "/v1/search",
    title: "Search",
    body: "Find products with natural language or a photo, including the India shop. Filter by category, price, color, or country. Each result is a product with ranked store offers and tracked buy links.",
    request: `POST /v1/search
{ "query": "wireless earbuds under 50", "limit": 5 }`,
    response: `{ "products": [ {
    "title": "…", "min_price": 24.99,
    "offers": [ { "merchant": "…", "price": { "amount": 24.99, "currency": "USD" },
                  "commission_rate": 0.08, "buy_url": "https://…" } ]
} ], "next_page": "…" }`,
  },
  {
    icon: Link2,
    path: "/v1/lookup",
    title: "Lookup",
    body: "Pass a Shoptosa product link, listing id, or product id. Get the structured product back with images, attributes, and every store that sells it.",
    request: `POST /v1/lookup
{ "url": "https://shoptosa.com/product/…" }`,
    response: `{ "title": "…", "images": [ … ], "attributes": { … },
  "offer_count": 3, "offers": [ … ] }`,
  },
  {
    icon: ShoppingBag,
    path: "/v1/checkout",
    title: "Checkout",
    body: "Turn Shoptosa seller listings into a ready bag. You get a checkout link and the estimated commission. Partner store offers check out on the merchant site through their buy link.",
    request: `POST /v1/checkout
{ "listing_ids": ["…", "…"] }`,
    response: `{ "status": "checkout_later", "checkout_url": "https://shoptosa.com/cart?graph_session=…",
  "total": 269.0, "estimated_commission": 21.52 }`,
  },
];

const TOOLS = [
  {
    icon: Library,
    title: "Collections",
    body: "Save products from search or lookup into named groups, then read them back with tracked buy links.",
    code: `POST /v1/collections { "name": "Gifts under 50" }
POST /v1/collections/{id}/items { "product_ids": ["…"] }`,
  },
  {
    icon: Webhook,
    title: "Webhooks",
    body: "A signed POST for every tracked click and conversion: product, title, your shopper id, query, order amount, and commission.",
    code: `POST /v1/webhooks { "url": "https://…", "events": ["click", "conversion"] }
Shoptosa-Signature: t=…,v1=HMAC-SHA256("t.body")`,
  },
  {
    icon: Globe,
    title: "Websites",
    body: "Register every domain that embeds the chat widget. The widget runs only on those domains. You can also reject other browser API calls from unregistered sites.",
    code: `POST /v1/websites { "domain": "shop.example.com" }`,
  },
  {
    icon: MessageSquare,
    title: "Chat widget",
    body: "Drop one script tag on a domain you registered. The chat answers only there. The secret key stays on our side.",
    code: `<script src="${API_HOST}/widget.js" data-widget="wgt_…" async></script>`,
  },
];

const FAQ = [
  {
    q: "What may I do with the API?",
    a: "Use it to build shopping tools on the published routes. Do not share secret keys, scan internal hosts, or bypass marketplace access rules. See the acceptable use policy at /acceptable-use.",
  },
  {
    q: "What is the Shoptosa Graph API?",
    a: "One API to find products and earn on sales. Use it for shopping assistants, chatbots, comparison tools, browser extensions, and recommendations.",
  },
  {
    q: "What data does it return?",
    a: "Titles, descriptions, images, categories, attributes, prices, availability, merchants, commission rates, and tracked buy links.",
  },
  {
    q: "Which stores are in the graph?",
    a: "Listings from Shoptosa sellers, plus partner store catalogs connected to Shoptosa. Search shows which store each offer comes from.",
  },
  {
    q: "How do I earn?",
    a: "Offers carry a commission rate. Send shoppers through the buy_url. Clicks and conversions from your keys show in the developer console.",
  },
  {
    q: "Can I use it from ChatGPT, Claude, or Gemini?",
    a: "Yes. Cursor and Claude can use remote MCP at /mcp with your x-api-key. ChatGPT Custom GPTs import the OpenAPI file as Actions. Setup steps are in Console → Docs.",
  },
  {
    q: "How do I start?",
    a: "Create a free developer account, make a key in the console, and call /v1/search. Every account starts with 30 free credits. 1 credit is ₹1. A catalog call costs 1 credit. India prices cost 1 credit per store (11 for all stores). US prices cost 1 credit per store (10 for all stores). Recharge from ₹500 when you need more. Keys allow 60 calls a minute.",
  },
];

function Code({ children }) {
  return (
    <pre className="bg-aura-bg border border-aura-border p-3 text-[11px] leading-relaxed overflow-x-auto whitespace-pre">{children}</pre>
  );
}

export default function DevelopersLanding() {
  const { user } = useAuth();
  const [open, setOpen] = useState(0);
  const start = user ? "/developers/console" : DEVELOPER_SIGNUP;
  const { data: health } = useQuery({
    queryKey: ["graph-health"],
    queryFn: async () => (await fetch(`${API_HOST}/v1/health`)).json(),
    staleTime: 5 * 60 * 1000,
  });

  const copyPrompt = async () => {
    await navigator.clipboard.writeText(STARTER_PROMPT);
    toast.success("Starter prompt copied. Paste it into ChatGPT, Claude, Gemini, or Cursor.");
  };

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-10 pt-28 pb-24">
      <section className="mb-20">
        <div className="text-[11px] tracking-luxe uppercase text-aura-cyan mb-4">For developers</div>
        <h1 className="font-serif text-4xl md:text-6xl tracking-tight leading-[1.05] max-w-3xl">
          Build any shopping agent on Shoptosa
        </h1>
        <p className="mt-5 text-base text-aura-muted max-w-2xl leading-relaxed">
          One API over Shoptosa seller listings and partner store catalogs. Search, look up, and check out,
          with a commission on every sale your app sends.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to={start} className="px-7 py-3 rounded-full btn-gradient text-sm font-semibold inline-flex items-center gap-2" data-testid="dev-get-key">
            {user ? "Open console" : "Get a free API key"} <ArrowRight className="w-4 h-4" />
          </Link>
          <a href={`${API_HOST}/v1/docs`} target="_blank" rel="noreferrer" className="px-7 py-3 rounded-full border border-white/20 text-sm font-semibold hover:border-aura-cyan hover:text-aura-cyan">
            API reference
          </a>
          <button type="button" onClick={copyPrompt} className="px-7 py-3 rounded-full border border-white/20 text-sm font-semibold hover:border-aura-accent hover:text-aura-accent inline-flex items-center gap-2" data-testid="dev-copy-prompt">
            <Copy className="w-4 h-4" /> Copy prompt
          </button>
        </div>
      </section>

      <section className="mb-20">
        <h2 className="font-serif text-3xl tracking-tight mb-2">Structured products in one call</h2>
        <p className="text-sm text-aura-muted mb-8 max-w-2xl">Your agent gets items it can act on: a price, a store, and a link to buy.</p>
        <div className="grid lg:grid-cols-3 gap-4">
          {ENDPOINTS.map(({ icon: Icon, path, title, body, request, response }) => (
            <div key={path} className="border border-aura-border bg-aura-surface/40 p-5 flex flex-col">
              <div className="flex items-center gap-2 mb-2">
                <Icon className="w-4 h-4 text-aura-accent" />
                <span className="font-medium">{title}</span>
                <code className="ml-auto text-[11px] text-aura-muted">{path}</code>
              </div>
              <p className="text-sm text-aura-muted leading-relaxed mb-4">{body}</p>
              <div className="mt-auto space-y-2">
                <Code>{request}</Code>
                <Code>{response}</Code>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-20">
        <h2 className="font-serif text-3xl tracking-tight mb-2">Everything around the search call</h2>
        <p className="text-sm text-aura-muted mb-8 max-w-2xl">Manage these in the console or over the same API key.</p>
        <div className="grid md:grid-cols-2 gap-4">
          {TOOLS.map(({ icon: Icon, title, body, code }) => (
            <div key={title} className="border border-aura-border bg-aura-surface/40 p-5">
              <div className="flex items-center gap-2 mb-2">
                <Icon className="w-4 h-4 text-aura-accent" />
                <span className="font-medium">{title}</span>
              </div>
              <p className="text-sm text-aura-muted leading-relaxed mb-4">{body}</p>
              <Code>{code}</Code>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-20 grid md:grid-cols-2 gap-6">
        <div className="border border-aura-border p-6">
          <Coins className="w-5 h-5 text-aura-accent mb-3" />
          <h3 className="font-serif text-2xl mb-2">Earn on sales</h3>
          <p className="text-sm text-aura-muted leading-relaxed">
            Offers include a commission rate when one is stored. When a shopper buys through your buy_url, the sale is credited to your key.
            Impressions, clicks, conversions, and estimated commission are in the console and at GET /v1/reports/commission.
          </p>
        </div>
        <div className="border border-aura-border p-6">
          <Bot className="w-5 h-5 text-aura-accent mb-3" />
          <h3 className="font-serif text-2xl mb-2">Made for agents</h3>
          <p className="text-sm text-aura-muted leading-relaxed mb-4">
            Web search returns articles and review pages. The graph returns SKUs with prices, stores, and buy links.
            Plug it into any model through MCP, or hand the model llms.txt.
          </p>
          <div className="flex flex-wrap gap-4 text-xs">
            {DOC_LINKS.map((l) => (
              <a key={l.href} href={l.href} target="_blank" rel="noreferrer" className="link-underline">{l.label}</a>
            ))}
          </div>
        </div>
      </section>

      <section className="mb-20 grid sm:grid-cols-3 gap-3">
        <div className="border border-aura-border p-5">
          <div className="text-[11px] tracking-luxe uppercase text-aura-muted">Shoptosa listings</div>
          <div className="font-serif text-3xl mt-1">{health?.catalog?.products ?? "—"}</div>
        </div>
        <div className="border border-aura-border p-5">
          <div className="text-[11px] tracking-luxe uppercase text-aura-muted">Shoptosa sellers</div>
          <div className="font-serif text-3xl mt-1">{health?.catalog?.merchants ?? "—"}</div>
        </div>
        <div className="border border-aura-border p-5">
          <div className="text-[11px] tracking-luxe uppercase text-aura-muted">To start</div>
          <div className="font-serif text-3xl mt-1">30 free credits</div>
          <p className="text-xs text-aura-muted mt-2">30 catalog calls to start. Then ₹1 a credit: 1 a call, and 1 per India or US store.</p>
        </div>
      </section>

      <section className="mb-20">
        <h2 className="font-serif text-3xl tracking-tight mb-6">FAQs</h2>
        <div className="border border-aura-border divide-y divide-aura-border">
          {FAQ.map((f, i) => (
            <div key={f.q}>
              <button type="button" onClick={() => setOpen(open === i ? -1 : i)} className="w-full text-left px-5 py-4 text-sm font-medium flex justify-between gap-4">
                {f.q}
                <span className="text-aura-muted">{open === i ? "−" : "+"}</span>
              </button>
              {open === i && <p className="px-5 pb-4 text-sm text-aura-muted leading-relaxed">{f.a}</p>}
            </div>
          ))}
        </div>
      </section>

      <section className="grid md:grid-cols-2 gap-6">
        <div className="border border-aura-accent/60 p-6">
          <h3 className="font-serif text-2xl mb-2">Start building</h3>
          <p className="text-sm text-aura-muted mb-5">Make a key and your first search takes a minute.</p>
          <Link to={start} className="px-6 py-2.5 rounded-full btn-accent text-sm font-semibold inline-flex items-center gap-2">
            {user ? "Open console" : "Create a developer account"} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="border border-aura-border p-6">
          <Store className="w-5 h-5 text-aura-accent mb-3" />
          <h3 className="font-serif text-2xl mb-2">Selling products?</h3>
          <p className="text-sm text-aura-muted mb-5">
            List on Shoptosa and your products reach the apps built on the graph. Seller accounts manage this from the dashboard.
          </p>
          <Link to="/auth?mode=register&role=seller" className="text-sm link-underline">Become a seller</Link>
        </div>
      </section>
    </div>
  );
}
